// src/cockpit4/components/DiagPlatformSelector.tsx
// ============================================
// 诊断平台勾选区（三个诊断功能共用）
//
// 功能：展示 6 大已接入 AI 厂家（豆包/文心/通义/元宝/DeepSeek/Kimi），
//       支持多选勾选 + 全选/全不选，用户可自定义要轮询的平台集合。
// ============================================

import { Check } from "lucide-react";
import { DIAG_PLATFORMS } from "../../runtime/GrowthFactory/geoPlatforms";
import type { DiagPlatformKey } from "../../runtime/GrowthFactory/types";

interface Props {
  /** 已勾选的平台集合 */
  selected: DiagPlatformKey[];
  /** 勾选变化回调 */
  onChange: (keys: DiagPlatformKey[]) => void;
}

export default function DiagPlatformSelector({ selected, onChange }: Props) {
  const allKeys = DIAG_PLATFORMS.map((p) => p.key);
  const allSelected = allKeys.every((k) => selected.includes(k));
  const someSelected = selected.length > 0 && !allSelected;

  const toggle = (key: DiagPlatformKey) => {
    if (selected.includes(key)) {
      onChange(selected.filter((k) => k !== key));
    } else {
      onChange([...selected, key]);
    }
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : [...allKeys]);
  };

  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-white">诊断平台</span>
          <span className="text-[9px] font-mono text-gray-600">
            已接入 {DIAG_PLATFORMS.length} 家 AI 厂家，勾选后轮询
          </span>
        </div>
        <button
          onClick={toggleAll}
          className="text-[10px] px-2 py-1 rounded border border-white/[0.08] text-gray-400 hover:text-white hover:border-cyan-500/40 transition-colors"
        >
          {allSelected ? "全不选" : someSelected ? "全选" : "全选"}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {DIAG_PLATFORMS.map((p) => {
          const on = selected.includes(p.key);
          return (
            <button
              key={p.key}
              onClick={() => toggle(p.key)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium border transition-all ${
                on
                  ? "text-white border-white/[0.14] bg-white/[0.06]"
                  : "text-gray-500 border-white/[0.05] bg-white/[0.01] hover:text-gray-300"
              }`}
            >
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ background: p.color, opacity: on ? 1 : 0.35 }}
              />
              <span
                className="w-4 h-4 rounded flex items-center justify-center border transition-colors"
                style={{
                  borderColor: on ? p.color : "rgba(255,255,255,0.12)",
                  background: on ? `${p.color}22` : "transparent",
                }}
              >
                {on && <Check size={10} style={{ color: p.color }} strokeWidth={3} />}
              </span>
              {p.name}
            </button>
          );
        })}
      </div>
      {selected.length === 0 && (
        <div className="text-[10px] text-amber-400/80 mt-2">
          至少勾选 1 个平台才能开始诊断
        </div>
      )}
    </div>
  );
}
